<template>
  <div class="billing-view">
    <div class="billing-header">
      <div>
        <h1 class="billing-title">
          {{ formTitle }}
        </h1>
        <p class="billing-subtitle mb-0">
          Registra la información fiscal del cliente con los datos requeridos para facturación.
        </p>
      </div>
    </div>

    <b-card no-body class="billing-shell">
      <b-overlay :show="loading" rounded="sm">
        <b-card-body class="billing-shell__body">
          <b-alert
            v-if="!resolvedCustomerId"
            variant="warning"
            show
            class="mb-0"
          >
            No se encontró un cliente seleccionado para registrar la razón social.
          </b-alert>

          <validation-observer v-else ref="billingFormObserver" tag="div">
            <b-form novalidate class="billing-form" @submit.prevent="validateForm">
              <b-alert
                v-if="isPublicGeneralReadonly"
                variant="info"
                show
                class="mb-2"
              >
                Esta razón social corresponde a <strong>Público en general</strong>. Se puede consultar, pero no editar desde el formulario.
              </b-alert>

              <fieldset :disabled="isPublicGeneralReadonly" class="billing-form__fieldset">
                <section class="billing-section">
                  <div class="billing-section__header">
                    <div>
                      <p class="billing-section__eyebrow mb-0">
                        Datos fiscales
                      </p>
                      <h2 class="billing-section__title mb-0">
                        Identificación del contribuyente
                      </h2>
                    </div>
                  </div>

                  <b-row>
                    <b-col sm="12" md="6" lg="4">
                      <validation-provider #default="{ errors }" name="RFC" tag="div" :rules="validationRules.rfc">
                        <b-form-group label="RFC" label-for="rfc" class="billing-form-group">
                          <b-form-input
                            id="rfc"
                            v-model.trim="billing.rfc"
                            type="text"
                            :state="errors[0] ? false : null"
                            autocomplete="off"
                            placeholder="Ej. XAXX010101000"
                            @input="sanitizeRfc"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" md="6" lg="8">
                      <validation-provider #default="{ errors }" name="Razón Social" tag="div" :rules="validationRules.corporateName">
                        <b-form-group label="Razón Social" label-for="corporate_name" class="billing-form-group">
                          <b-form-input
                            id="corporate_name"
                            v-model.trim="billing.corporate_name"
                            :state="errors[0] ? false : null"
                            autocomplete="off"
                            placeholder="Ingresa la razón social"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" md="6" lg="4">
                      <validation-provider #default="{ errors }" name="Correo de facturación" tag="div" :rules="validationRules.email">
                        <b-form-group label="Correo de facturación" label-for="email" class="billing-form-group">
                          <b-form-input
                            id="email"
                            v-model.trim="billing.email"
                            :state="errors[0] ? false : null"
                            autocomplete="off"
                            inputmode="email"
                            placeholder="correo@empresa.com"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" md="6" lg="4">
                      <validation-provider #default="{ errors }" name="Régimen Fiscal" tag="div" :rules="validationRules.fiscalRegime">
                        <b-form-group label="Régimen Fiscal" label-for="fiscal_regime" class="billing-form-group">
                          <b-form-select
                            id="fiscal_regime"
                            v-model="billing.fiscal_regime"
                            :state="errors[0] ? false : null"
                            :options="fiscalRegimeOptions"
                          />
                          <small class="text-muted d-block billing-help-text">Selecciona la clave exactamente como aparece en la Constancia de Situación Fiscal.</small>
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" md="6" lg="4">
                      <validation-provider #default="{ errors }" name="Uso de CFDI" tag="div" :rules="validationRules.cfdiUse">
                        <b-form-group label="Uso de CFDI" label-for="cfdi_use" class="billing-form-group">
                          <b-form-select
                            id="cfdi_use"
                            v-model="billing.cfdi_use"
                            :state="errors[0] ? false : null"
                            :options="filteredCfdiUseOptions"
                            :disabled="isCfdiUseDisabled"
                          />
                          <small class="text-muted d-block billing-help-text">{{ cfdiUseHelpText }}</small>
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </section>

                <section class="billing-section billing-section--muted">
                  <div class="billing-section__header">
                    <div>
                      <p class="billing-section__eyebrow mb-0">
                        Domicilio fiscal
                      </p>
                      <h2 class="billing-section__title mb-0">
                        Ubicación registrada ante el SAT
                      </h2>
                    </div>
                  </div>

                  <b-row>
                    <b-col sm="12" md="6" lg="3">
                      <validation-provider #default="{ errors }" name="C.P." tag="div" :rules="validationRules.postalCode">
                        <b-form-group label="C.P." label-for="postal_code" class="billing-form-group">
                          <b-form-input
                            id="postal_code"
                            v-model.trim="billing.postal_code"
                            :state="errors[0] ? false : null"
                            autocomplete="off"
                            placeholder="Ingresa el C.P."
                            inputmode="numeric"
                            maxlength="5"
                            @input="sanitizePostalCode"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" md="6" lg="5">
                      <validation-provider #default="{ errors }" name="Calle" tag="div" :rules="validationRules.street">
                        <b-form-group label="Calle" label-for="street" class="billing-form-group">
                          <b-form-input
                            id="street"
                            v-model.trim="billing.street"
                            :state="errors[0] ? false : null"
                            autocomplete="off"
                            placeholder="Ingresa la calle"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" md="6" lg="2">
                      <validation-provider #default="{ errors }" name="Número Exterior" tag="div" :rules="validationRules.extNumber">
                        <b-form-group label="Número Exterior" label-for="ext_number" class="billing-form-group">
                          <b-form-input
                            id="ext_number"
                            v-model.trim="billing.ext_number"
                            :state="errors[0] ? false : null"
                            autocomplete="off"
                            placeholder="Ej. 123"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" md="6" lg="2">
                      <validation-provider #default="{ errors }" name="Número Interior" tag="div" :rules="validationRules.intNumber">
                        <b-form-group label="Número Interior" label-for="int_number" class="billing-form-group">
                          <b-form-input
                            id="int_number"
                            v-model.trim="billing.int_number"
                            :state="errors[0] ? false : null"
                            autocomplete="off"
                            placeholder="Opcional"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" md="6" lg="4">
                      <validation-provider #default="{ errors }" name="Colonia" tag="div" :rules="validationRules.suburb">
                        <b-form-group label="Colonia" label-for="suburb" class="billing-form-group">
                          <b-form-select
                            v-if="shouldUseSuburbSelect"
                            id="suburb"
                            v-model="billing.suburb"
                            :state="errors[0] ? false : null"
                            :options="suburbOptions"
                            :disabled="postalLookupLoading || !hasSuburbOptions"
                          />
                          <b-form-input
                            v-else
                            id="suburb"
                            v-model.trim="billing.suburb"
                            :state="errors[0] ? false : null"
                            autocomplete="off"
                            placeholder="Ingresa la colonia"
                          />
                          <small class="text-muted d-block billing-help-text">{{ suburbHelpText }}</small>
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" md="6" lg="4">
                      <validation-provider #default="{ errors }" name="Localidad" tag="div" :rules="validationRules.locality">
                        <b-form-group label="Localidad" label-for="locality" class="billing-form-group">
                          <b-form-input
                            id="locality"
                            v-model.trim="billing.locality"
                            :state="errors[0] ? false : null"
                            autocomplete="off"
                            placeholder="Ingresa la localidad"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" md="6" lg="4">
                      <validation-provider #default="{ errors }" name="Municipio/Ciudad" tag="div" :rules="validationRules.city">
                        <b-form-group label="Municipio/Ciudad" label-for="city" class="billing-form-group">
                          <b-form-input
                            id="city"
                            v-model.trim="billing.city"
                            :state="errors[0] ? false : null"
                            autocomplete="off"
                            placeholder="Ingresa municipio o ciudad"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" md="6" lg="6">
                      <validation-provider #default="{ errors }" name="Estado" tag="div" :rules="validationRules.state">
                        <b-form-group label="Estado" label-for="state" class="billing-form-group">
                          <b-form-select
                            id="state"
                            v-model="billing.state"
                            :state="errors[0] ? false : null"
                            :options="stateOptions"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col sm="12" lg="6">
                      <validation-provider #default="{ errors }" name="Documento CSF" tag="div" :rules="validationRules.csfDocument">
                        <b-form-group label="Documento CSF (URL)" label-for="csf_document" class="billing-form-group">
                          <b-form-input
                            id="csf_document"
                            v-model.trim="billing.csf_document"
                            :state="errors[0] ? false : null"
                            autocomplete="off"
                            placeholder="https://..."
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </section>
              </fieldset>

              <div class="billing-actions">
                <b-button
                  v-if="!isPublicGeneralReadonly"
                  type="submit"
                  variant="primary"
                  class="billing-actions__primary"
                >
                  {{ submitLabel }}
                </b-button>
                <b-button
                  variant="outline-secondary"
                  :to="{
                    name: 'billing',
                    params: resolvedCustomerId ? { customerId: resolvedCustomerId } : {},
                  }"
                >
                  {{ $t('cancel') }}
                </b-button>
              </div>
            </b-form>
          </validation-observer>
        </b-card-body>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  required,
  email,
  digits,
  regex,
  url,
  rfc as rfcValidation,
} from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BRow,
  BCol,
  BButton,
  BCard,
  BCardBody,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BOverlay,
  BAlert,
} from 'bootstrap-vue'

const emptyBilling = () => ({
  rfc: '',
  corporate_name: '',
  postal_code: '',
  street: '',
  ext_number: '',
  int_number: '',
  suburb: '',
  locality: '',
  city: '',
  state: '',
  email: '',
  cfdi_use: '',
  fiscal_regime: '',
  csf_document: '',
})

const stateOptions = [
  { value: '', text: 'Selecciona un estado', disabled: true },
  { value: 'Aguascalientes', text: 'Aguascalientes' },
  { value: 'Baja California', text: 'Baja California' },
  { value: 'Baja California Sur', text: 'Baja California Sur' },
  { value: 'Campeche', text: 'Campeche' },
  { value: 'Chiapas', text: 'Chiapas' },
  { value: 'Chihuahua', text: 'Chihuahua' },
  { value: 'Ciudad de México', text: 'Ciudad de México' },
  { value: 'Coahuila', text: 'Coahuila' },
  { value: 'Colima', text: 'Colima' },
  { value: 'Durango', text: 'Durango' },
  { value: 'Guanajuato', text: 'Guanajuato' },
  { value: 'Guerrero', text: 'Guerrero' },
  { value: 'Hidalgo', text: 'Hidalgo' },
  { value: 'Jalisco', text: 'Jalisco' },
  { value: 'Estado de México', text: 'Estado de México' },
  { value: 'Michoacán', text: 'Michoacán' },
  { value: 'Morelos', text: 'Morelos' },
  { value: 'Nayarit', text: 'Nayarit' },
  { value: 'Nuevo León', text: 'Nuevo León' },
  { value: 'Oaxaca', text: 'Oaxaca' },
  { value: 'Puebla', text: 'Puebla' },
  { value: 'Querétaro', text: 'Querétaro' },
  { value: 'Quintana Roo', text: 'Quintana Roo' },
  { value: 'San Luis Potosí', text: 'San Luis Potosí' },
  { value: 'Sinaloa', text: 'Sinaloa' },
  { value: 'Sonora', text: 'Sonora' },
  { value: 'Tabasco', text: 'Tabasco' },
  { value: 'Tamaulipas', text: 'Tamaulipas' },
  { value: 'Tlaxcala', text: 'Tlaxcala' },
  { value: 'Veracruz', text: 'Veracruz' },
  { value: 'Yucatán', text: 'Yucatán' },
  { value: 'Zacatecas', text: 'Zacatecas' },
]

const fiscalRegimeOptions = [
  { value: '', text: 'Selecciona un régimen fiscal', disabled: true },
  { value: '601', text: '601 - General de Ley Personas Morales' },
  { value: '603', text: '603 - Personas Morales con Fines no Lucrativos' },
  { value: '605', text: '605 - Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { value: '606', text: '606 - Arrendamiento' },
  { value: '607', text: '607 - Régimen de Enajenación o Adquisición de Bienes' },
  { value: '608', text: '608 - Demás ingresos' },
  { value: '610', text: '610 - Residentes en el Extranjero sin Establecimiento Permanente en México' },
  { value: '611', text: '611 - Ingresos por Dividendos (socios y accionistas)' },
  { value: '612', text: '612 - Personas Físicas con Actividades Empresariales y Profesionales' },
  { value: '614', text: '614 - Ingresos por intereses' },
  { value: '615', text: '615 - Régimen de los ingresos por obtención de premios' },
  { value: '616', text: '616 - Sin obligaciones fiscales' },
  { value: '620', text: '620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos' },
  { value: '621', text: '621 - Incorporación Fiscal' },
  { value: '622', text: '622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { value: '623', text: '623 - Opcional para Grupos de Sociedades' },
  { value: '624', text: '624 - Coordinados' },
  { value: '625', text: '625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
  { value: '626', text: '626 - Régimen Simplificado de Confianza' },
  { value: '628', text: '628 - Hidrocarburos' },
]

const cfdiUseCatalog = {
  general: [
    { value: 'G01', text: 'G01 - Adquisición de mercancías' },
    { value: 'G02', text: 'G02 - Devoluciones, descuentos o bonificaciones' },
    { value: 'G03', text: 'G03 - Gastos en general' },
  ],
  investments: [
    { value: 'I01', text: 'I01 - Construcciones' },
    { value: 'I02', text: 'I02 - Mobiliario y equipo de oficina por inversiones' },
    { value: 'I03', text: 'I03 - Equipo de transporte' },
    { value: 'I04', text: 'I04 - Equipo de cómputo y accesorios' },
    { value: 'I05', text: 'I05 - Dados, troqueles, moldes, matrices y herramental' },
    { value: 'I06', text: 'I06 - Comunicaciones telefónicas' },
    { value: 'I07', text: 'I07 - Comunicaciones satelitales' },
    { value: 'I08', text: 'I08 - Otra maquinaria y equipo' },
  ],
  personalDeductions: [
    { value: 'D01', text: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
    { value: 'D02', text: 'D02 - Gastos médicos por incapacidad o discapacidad' },
    { value: 'D03', text: 'D03 - Gastos funerales' },
    { value: 'D04', text: 'D04 - Donativos' },
    { value: 'D05', text: 'D05 - Intereses reales efectivamente pagados por créditos hipotecarios' },
    { value: 'D06', text: 'D06 - Aportaciones voluntarias al SAR' },
    { value: 'D07', text: 'D07 - Primas por seguros de gastos médicos' },
    { value: 'D08', text: 'D08 - Gastos de transportación escolar obligatoria' },
    { value: 'D09', text: 'D09 - Depósitos en cuentas para el ahorro, primas con base en planes de pensiones' },
    { value: 'D10', text: 'D10 - Pagos por servicios educativos (colegiaturas)' },
  ],
  others: [
    { value: 'S01', text: 'S01 - Sin efectos fiscales' },
    { value: 'CP01', text: 'CP01 - Pagos' },
    { value: 'CN01', text: 'CN01 - Nómina' },
  ],
}

const moralRegimes = ['601', '603', '620', '623', '624']
const businessPhysicalRegimes = ['606', '607', '608', '610', '611', '612', '614', '615', '621', '622', '625', '626', '628']

const buildCfdiUseOptions = groups => {
  const labels = {
    general: 'Usos generales',
    investments: 'Inversiones',
    personalDeductions: 'Deducciones personales',
    others: 'Otros usos',
  }

  return [
    { value: '', text: 'Selecciona el uso de CFDI', disabled: true },
    ...groups.map(group => ({
      label: labels[group],
      options: cfdiUseCatalog[group],
    })),
  ]
}

const normalizeStateName = value => {
  const stateAliases = {
    'Coahuila de Zaragoza': 'Coahuila',
    México: 'Estado de México',
    'Michoacán de Ocampo': 'Michoacán',
    'Veracruz de Ignacio de la Llave': 'Veracruz',
  }

  return stateAliases[value] || value || ''
}

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    BRow,
    BCol,
    BButton,
    BCard,
    BCardBody,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BOverlay,
    BAlert,
  },
  data() {
    return {
      required,
      email,
      digits,
      regex,
      url,
      rfcValidation,
      stateOptions,
      fiscalRegimeOptions,
      billing: emptyBilling(),
      postalLookupLoading: false,
      postalLookupRequestId: 0,
      postalLookupError: '',
      isPublicGeneralTaxInformation: false,
      suburbOptions: [{ value: '', text: 'Ingresa un C.P. para consultar colonias', disabled: true }],
      validationRules: {
        rfc: {
          required: true,
          rfc: true,
        },
        corporateName: 'required',
        postalCode: 'required|digits:5',
        street: '',
        extNumber: '',
        intNumber: '',
        suburb: '',
        locality: '',
        city: '',
        state: '',
        email: 'email',
        cfdiUse: 'required',
        fiscalRegime: 'required',
        csfDocument: 'url',
      },
    }
  },
  computed: {
    ...mapGetters('billing', ['selectedTaxInformation', 'loading']),
    ...mapGetters('customers', ['selectedCustomerResourceId']),
    ...mapGetters('auth', ['currentUser']),
    resolvedCustomerId() {
      const fromRoute = this.$route.params.customerId
      const fromStore = this.selectedCustomerResourceId
      const fromScopedRole = this.currentUser?.scoped_roles?.[0]?.role_resource_id
      return fromRoute || fromStore || fromScopedRole || null
    },
    taxInformationId() {
      return this.$route.query.id || null
    },
    isEditMode() {
      return !!this.taxInformationId
    },
    formTitle() {
      return this.isEditMode ? 'Editar Razón Social' : 'Alta de Facturación'
    },
    submitLabel() {
      return this.isEditMode ? this.$t('edit') : 'Registrar Razón Social'
    },
    isPublicGeneralReadonly() {
      return this.isEditMode && this.isPublicGeneralTaxInformation
    },
    hasSuburbOptions() {
      return this.suburbOptions.some(option => !option.disabled)
    },
    shouldUseSuburbSelect() {
      return this.postalLookupLoading || this.hasSuburbOptions
    },
    suburbHelpText() {
      if (this.postalLookupLoading) {
        return 'Consultando colonias disponibles para ese código postal...'
      }

      if (this.postalLookupError) {
        return this.postalLookupError
      }

      if (this.hasSuburbOptions) {
        return 'Selecciona la colonia correspondiente al código postal capturado.'
      }

      if (this.billing.postal_code.length === 5) {
        return 'No se encontraron colonias para ese código postal. Puedes capturarla manualmente.'
      }

      return 'Al capturar un C.P. válido, el sistema intentará sugerir las colonias disponibles.'
    },
    isCfdiUseDisabled() {
      return !this.billing.fiscal_regime
    },
    filteredCfdiUseOptions() {
      const regime = this.billing.fiscal_regime

      if (!regime) {
        return [{ value: '', text: 'Selecciona primero el régimen fiscal', disabled: true }]
      }

      if (regime === '616') {
        return buildCfdiUseOptions(['others']).map(option => {
          if (!option.options) return option

          return {
            ...option,
            options: option.options.filter(item => item.value === 'S01'),
          }
        })
      }

      if (regime === '605') {
        return buildCfdiUseOptions(['personalDeductions', 'others'])
      }

      if (moralRegimes.includes(regime) || businessPhysicalRegimes.includes(regime)) {
        return buildCfdiUseOptions(['general', 'investments', 'others'])
      }

      return buildCfdiUseOptions(['general', 'others'])
    },
    cfdiUseHelpText() {
      if (!this.billing.fiscal_regime) {
        return 'Selecciona primero el régimen fiscal para habilitar las claves compatibles de uso de CFDI.'
      }

      if (this.billing.fiscal_regime === '616') {
        return 'Para RFC genérico o sin obligaciones fiscales, se restringe a S01 - Sin efectos fiscales.'
      }

      if (this.billing.fiscal_regime === '605') {
        return 'Para Sueldos y Salarios se priorizan deducciones personales y otros usos compatibles.'
      }

      return 'Solo se muestran usos de CFDI compatibles con el régimen fiscal seleccionado.'
    },
  },
  watch: {
    'billing.postal_code': function (newValue, oldValue) {
      if (newValue === oldValue) return

      if (newValue.length === 5) {
        this.lookupPostalCodeData(newValue)
        return
      }

      this.resetPostalLookup()
    },
    'billing.fiscal_regime': function (newValue) {
      if (!newValue) {
        this.billing.cfdi_use = ''
        return
      }

      const allowedValues = this.filteredCfdiUseOptions
        .flatMap(option => option.options || [])
        .map(option => option.value)

      if (!allowedValues.includes(this.billing.cfdi_use)) {
        this.billing.cfdi_use = ''
      }
    },
  },
  async created() {
    if (this.resolvedCustomerId) {
      this.selectCustomerId(this.resolvedCustomerId)
    }

    if (this.isEditMode) {
      await this.loadTaxInformation()
    }
  },
  methods: {
    ...mapActions('billing', [
      'fetchTaxInformations',
      'createTaxInformation',
      'updateTaxInformation',
      'lookupPostalCode',
    ]),
    ...mapMutations('customers', {
      selectCustomerId: 'SELECT_CUSTOMER_ID',
    }),
    toast(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: 'EditIcon',
          variant,
        },
      })
    },
    getErrorMessage(error, fallback = 'Ocurrió un error.') {
      const payload = error?.response?.data || error?.data || {}
      if (Array.isArray(payload.messages) && payload.messages.length > 0) return payload.messages[0]
      if (typeof payload.error === 'string') return payload.error
      if (typeof error?.message === 'string') return error.message
      return fallback
    },
    hydrateBilling(taxInformation) {
      this.isPublicGeneralTaxInformation = !!taxInformation?.is_public_general
      this.billing = {
        ...emptyBilling(),
        ...taxInformation,
      }
      this.normalizeBillingFields()
    },
    resetPostalLookup() {
      this.postalLookupRequestId += 1
      this.postalLookupLoading = false
      this.postalLookupError = ''
      this.suburbOptions = [{ value: '', text: 'Ingresa un C.P. para consultar colonias', disabled: true }]
    },
    normalizeBillingFields() {
      this.billing.rfc = String(this.billing.rfc || '').toUpperCase().replace(/[^A-Z0-9&Ñ]/g, '')
      this.billing.postal_code = String(this.billing.postal_code || '').replace(/\D/g, '').slice(0, 5)
      this.billing.cfdi_use = String(this.billing.cfdi_use || '').toUpperCase().replace(/\s+/g, '').slice(0, 4)
      this.billing.fiscal_regime = String(this.billing.fiscal_regime || '').replace(/\D/g, '').slice(0, 3)
    },
    sanitizeRfc(value) {
      this.billing.rfc = String(value || '').toUpperCase().replace(/[^A-Z0-9&Ñ]/g, '')
    },
    sanitizePostalCode(value) {
      this.billing.postal_code = String(value || '').replace(/\D/g, '').slice(0, 5)
    },
    applyPostalLookupResult(result) {
      const colonies = Array.from(new Set(result.colonies || [])).filter(Boolean)

      this.suburbOptions = [
        { value: '', text: 'Selecciona una colonia', disabled: true },
        ...colonies.map(colony => ({ value: colony, text: colony })),
      ]

      if (!colonies.includes(this.billing.suburb)) {
        this.billing.suburb = colonies.length === 1 ? colonies[0] : ''
      }

      if (result.state) this.billing.state = normalizeStateName(result.state)
      if (result.city) this.billing.city = result.city
      if (result.locality) this.billing.locality = result.locality
    },
    async lookupPostalCodeData(postalCode) {
      if (!this.resolvedCustomerId) {
        this.resetPostalLookup()
        return
      }

      const requestId = this.postalLookupRequestId + 1
      this.postalLookupRequestId = requestId
      this.postalLookupLoading = true
      this.postalLookupError = ''
      this.suburbOptions = [{ value: '', text: 'Consultando colonias...', disabled: true }]

      try {
        const result = await this.lookupPostalCode({
          customerId: this.resolvedCustomerId,
          postalCode,
        })

        if (requestId !== this.postalLookupRequestId) return

        this.applyPostalLookupResult(result)
      } catch (error) {
        if (requestId !== this.postalLookupRequestId) return

        this.suburbOptions = [{ value: '', text: 'No se pudieron consultar colonias', disabled: true }]
        this.postalLookupError = this.getErrorMessage(error, 'No fue posible consultar colonias automáticamente para ese C.P. Puedes capturar la colonia manualmente.')
      } finally {
        if (requestId === this.postalLookupRequestId) {
          this.postalLookupLoading = false
        }
      }
    },
    sanitizeCfdiUse(value) {
      this.billing.cfdi_use = String(value || '').toUpperCase().replace(/\s+/g, '').slice(0, 4)
    },
    sanitizeFiscalRegime(value) {
      this.billing.fiscal_regime = String(value || '').replace(/\D/g, '').slice(0, 3)
    },
    sanitizeBilling() {
      return {
        rfc: (this.billing.rfc || '').trim(),
        corporate_name: (this.billing.corporate_name || '').trim(),
        postal_code: (this.billing.postal_code || '').trim(),
        street: (this.billing.street || '').trim(),
        ext_number: (this.billing.ext_number || '').trim(),
        int_number: (this.billing.int_number || '').trim(),
        suburb: (this.billing.suburb || '').trim(),
        locality: (this.billing.locality || '').trim(),
        city: (this.billing.city || '').trim(),
        state: (this.billing.state || '').trim(),
        email: (this.billing.email || '').trim(),
        cfdi_use: (this.billing.cfdi_use || '').trim(),
        fiscal_regime: (this.billing.fiscal_regime || '').trim(),
        csf_document: (this.billing.csf_document || '').trim(),
      }
    },
    async validateForm() {
      const isValid = await this.$refs.billingFormObserver.validate()
      if (!isValid) return

      await this.submitForm()
    },
    async loadTaxInformation() {
      if (!this.resolvedCustomerId || !this.taxInformationId) return

      try {
        if (this.selectedTaxInformation?.id === this.taxInformationId) {
          this.hydrateBilling(this.selectedTaxInformation)
          return
        }

        const response = await this.fetchTaxInformations({
          customerId: this.resolvedCustomerId,
          pagination: {
            page: 1,
            per_page: 200,
            all: true,
          },
        })
        const taxInformation = (response.data || []).find(
          item => item.id === this.taxInformationId,
        )

        if (!taxInformation) {
          this.toast('No se encontró la razón social solicitada.', 'warning')
          this.$router.push({
            name: 'billing',
            params: this.resolvedCustomerId ? { customerId: this.resolvedCustomerId } : {},
          })
          return
        }

        this.hydrateBilling(taxInformation)
      } catch (error) {
        this.toast(this.getErrorMessage(error, 'No fue posible cargar la razón social.'), 'danger')
      }
    },
    async submitForm() {
      if (!this.resolvedCustomerId) {
        this.toast('No se encontró el cliente para guardar la razón social.', 'warning')
        return
      }

      const payload = this.sanitizeBilling()

      try {
        if (this.isEditMode) {
          await this.updateTaxInformation({
            customerId: this.resolvedCustomerId,
            taxInformationId: this.taxInformationId,
            taxInformation: payload,
          })
          this.toast('Razón social actualizada.', 'success')
        } else {
          await this.createTaxInformation({
            customerId: this.resolvedCustomerId,
            taxInformation: payload,
          })
          this.toast('Razón social registrada.', 'success')
        }

        this.$router.push({
          name: 'billing',
          params: this.resolvedCustomerId ? { customerId: this.resolvedCustomerId } : {},
        })
      } catch (error) {
        this.toast(this.getErrorMessage(error, 'No fue posible guardar la razón social.'), 'danger')
      }
    },
  },
}
</script>

<style scoped>
.billing-view {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.billing-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.billing-title {
  margin-bottom: 0.35rem;
  color: #3f3d64;
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.billing-subtitle {
  max-width: 42rem;
  color: #6b7280;
  font-size: 0.98rem;
  line-height: 1.5;
}

.billing-shell {
  border: 0;
  border-radius: 20px;
  box-shadow: 0 24px 55px rgba(31, 41, 55, 0.08);
}

.billing-shell__body {
  padding: 1.75rem;
}

.billing-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.billing-form__fieldset {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.billing-section {
  padding: 1.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfbfd 100%);
}

.billing-section--muted {
  background: linear-gradient(180deg, #fbfcff 0%, #f6f8fc 100%);
}

.billing-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.billing-section__eyebrow {
  color: #6d28d9;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.billing-section__title {
  color: #1f2937;
  font-size: 1.05rem;
  font-weight: 700;
}

.billing-form-group {
  margin-bottom: 1rem;
}

.billing-form-group ::v-deep label {
  margin-bottom: 0.45rem;
  color: #4b5563;
  font-size: 0.85rem;
  font-weight: 700;
}

.billing-form-group ::v-deep .form-control {
  min-height: 48px;
  border: 1px solid #d6dbe6;
  border-radius: 12px;
  box-shadow: none;
  background-image: none !important;
  padding-right: 0.9rem !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.billing-form-group ::v-deep .form-control:focus {
  border-color: #6d28d9;
  box-shadow: 0 0 0 4px rgba(109, 40, 217, 0.12);
}

.billing-form-group ::v-deep .custom-select {
  min-height: 48px;
  border: 1px solid #d6dbe6;
  border-radius: 12px;
  box-shadow: none;
  background-image: none !important;
  padding-right: 2rem !important;
}

.billing-form-group ::v-deep .custom-select:focus {
  border-color: #6d28d9;
  box-shadow: 0 0 0 4px rgba(109, 40, 217, 0.12);
}

.billing-form-group ::v-deep .form-control.is-invalid,
.billing-form-group ::v-deep .custom-select.is-invalid {
  border-color: #f87171;
  box-shadow: none;
  background-image: none !important;
}

.billing-form-group ::v-deep .form-control.is-invalid:focus,
.billing-form-group ::v-deep .custom-select.is-invalid:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
}

.billing-form-group ::v-deep .text-danger {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.83rem;
  font-weight: 500;
  line-height: 1.35;
}

.billing-help-text {
  margin-top: 0.35rem;
}

.billing-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-top: 0.25rem;
}

.billing-actions__primary {
  min-width: 220px;
}

@media (max-width: 767px) {
  .billing-shell__body {
    padding: 1rem;
  }

  .billing-section {
    padding: 1rem;
  }

  .billing-title {
    font-size: 1.65rem;
  }

  .billing-actions {
    flex-direction: column;
  }

  .billing-actions__primary {
    width: 100%;
  }
}
</style>
